import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { HardDrive, CheckCircle2, AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Storage health widget — surfaces whether the durable object storage
 * backend is reachable. NEVER displays secrets; the backend returns only
 * `{configured, reachable, app_prefix, provider, last_checked, warning}`.
 *
 * Why: K8s pod local disk does not survive redeploys. The admin needs a
 * one-glance signal that uploads will persist before bulk-re-uploading
 * the 48 product images that were lost in the prior deploy.
 */
export default function StorageHealthWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/admin/uploads/storage-health");
      setData(r.data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const ready = data && data.storage_configured && data.storage_reachable;
  const tone = ready
    ? "border-emerald-300 bg-emerald-50/40"
    : (data && data.storage_configured ? "border-amber-300 bg-amber-50/40" : "border-red-300 bg-red-50/40");

  return (
    <div className={`bg-white border ${tone} rounded-md p-5 mb-10`} data-testid="storage-health-widget">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <HardDrive size={16} className="text-zinc-500 mt-0.5" />
          <div>
            <h2 className="font-display tracking-tight">Object storage</h2>
            <p className="text-[11px] text-zinc-500 mt-0.5">
              Product image uploads land in durable storage and survive redeploys.
            </p>
          </div>
        </div>
        <button onClick={load} className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1" data-testid="storage-refresh">
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
        <Cell label="Status" value={loading ? "Checking…" : ready ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-medium" data-testid="storage-status-ready">
            <CheckCircle2 size={12} /> Ready
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-amber-700 font-medium" data-testid="storage-status-not-ready">
            <AlertTriangle size={12} /> Not ready
          </span>
        )} />
        <Cell label="Configured" value={data ? (data.storage_configured ? "yes" : "no") : "—"} testid="storage-configured" />
        <Cell label="Reachable" value={data ? (data.storage_reachable ? "yes" : "no") : "—"} testid="storage-reachable" />
        <Cell label="Prefix" value={data?.app_prefix || "—"} mono testid="storage-prefix" />
        <Cell label="Provider" value={data?.provider || "—"} mono />
        <Cell label="Last checked" value={data?.last_checked ? new Date(data.last_checked).toLocaleTimeString() : "—"} mono />
      </div>

      {data?.warning && (
        <div className="mt-4 text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded p-3" data-testid="storage-warning">
          {data.warning}
        </div>
      )}
      {error && (
        <div className="mt-4 text-sm bg-red-50 border border-red-200 text-red-900 rounded p-3">
          Failed to load storage health: {error}
        </div>
      )}
    </div>
  );
}

function Cell({ label, value, mono, testid }) {
  return (
    <div className="border border-zinc-200 rounded p-2.5" data-testid={testid}>
      <div className="text-[9px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className={`text-zinc-800 break-all ${mono ? "font-mono text-[11px]" : ""}`}>{value}</div>
    </div>
  );
}
